import {
  Ban,
  CheckCircle2,
  CircleAlert,
  HelpCircle,
  Info,
  Loader,
  ReceiptText,
  RefreshCw,
  RotateCcw,
  Search,
  ShieldAlert,
  Undo2,
  Wallet,
} from 'lucide-react'
import { useId } from 'react'

import { Button, StatusMessage } from '@krp/ui-kit'

import { QueryState } from '@krp/sale-ui'
import '../../../shared/ui/aqsi.css'
import { StateBadge } from '@krp/sale-ui'
import '@krp/sale-ui/sale.css'
import type { RefundActions, RefundSourceLineVm, RefundVm } from '../model/types'
import './refunds.css'

/** Фаза команды — своим значком, не только цветом: отправляется / исход неизвестен / не дошла. */
const PHASE_ICON = {
  sending: <Loader size={14} aria-hidden="true" />,
  unknown: <HelpCircle size={14} aria-hidden="true" />,
  'not-arrived': <RotateCcw size={14} aria-hidden="true" />,
} as const

const BLOCKED_ACTION_TEXT = {
  rmk: 'Открыть РМК',
  sale: 'Открыть продажу',
  refund: 'Открыть тот возврат',
} as const

function SourceLine({
  line,
  locked,
  onChange,
}: {
  line: RefundSourceLineVm
  locked: boolean
  onChange: (quantity: string) => void
}) {
  const id = useId()
  const errorId = `${id}-error`
  return (
    <li
      className="sale__line"
      data-testid="aqsi-refund-line"
      data-invalid={line.error !== null || undefined}
      data-exhausted={line.exhausted || undefined}
    >
      <div className="sale__line-body">
        <div className="aqsi-refund__line-name">
          <strong>
            {line.ordinal}. {line.name}
          </strong>
          <span className="aqsi-card__text">
            Продано {line.soldText} · возвращено {line.returnedText} · можно вернуть {line.availableText} · цена{' '}
            {line.priceText}
          </span>
        </div>
        <label className="sale__qty" htmlFor={id}>
          <span>Вернуть</span>
          <input
            id={id}
            className="sale__qty-input"
            inputMode="decimal"
            placeholder="0"
            value={line.quantity}
            disabled={locked || line.exhausted}
            aria-invalid={line.error !== null}
            aria-describedby={line.error !== null ? errorId : undefined}
            onChange={(event) => onChange(event.target.value)}
          />
        </label>
      </div>
      {line.exhausted && (
        <p className="sale__error" data-tone="neutral">
          Позиция уже возвращена полностью
        </p>
      )}
      {line.error !== null && (
        <p id={errorId} className="sale__error" role="alert">
          {line.error}
        </p>
      )}
    </li>
  )
}

/**
 * Возврат по чеку кабинета (C4b). Возврат — отдельная операция, не отмена
 * продажи: служба создаёт его по чеку журнала ЭТОГО rid, деньги возвращаются
 * способом исходной оплаты, чек возврата пробивается отдельно. Неизвестный исход
 * запрещает повтор: «Проверить статус» только читает, ручная сверка — отдельное
 * окно с причиной.
 */
export function RefundScreen({ vm, actions }: { vm: RefundVm; actions: RefundActions }) {
  const busy = vm.command?.phase === 'sending'
  return (
    <section className="aqsi-screen" aria-labelledby="aqsi-refund-title">
      <header className="aqsi-toolbar">
        <h2 id="aqsi-refund-title">
          <Undo2 size={18} aria-hidden="true" /> {vm.title}
        </h2>
        <Button variant="ghost" icon={<RefreshCw size={16} aria-hidden="true" />} onClick={actions.checkStatus}>
          Проверить статус
        </Button>
      </header>

      <QueryState query={vm.receiptQuery} what="Чек" emptyText="Чек не найден" onRetry={actions.reload} />
      {vm.receiptQuery.status === 'ready' && vm.seatQuery !== null && vm.seatQuery.status !== 'ready' && (
        <QueryState query={vm.seatQuery} what="Касса" emptyText="Касса не найдена" onRetry={actions.reload} />
      )}

      {vm.header !== null && (
        <dl className="aqsi-facts" aria-label="Исходный чек">
          <dt>Время</dt>
          <dd>{vm.header.atText}</dd>
          <dt>Итого</dt>
          <dd>{vm.header.totalText}</dd>
          <dt>Оплата</dt>
          <dd>{vm.header.paymentText}</dd>
          <dt>Возвраты</dt>
          <dd>{vm.header.returnedText}</dd>
        </dl>
      )}

      {vm.command !== null && (
        <p
          className="aqsi-panel"
          data-tone={vm.command.phase === 'unknown' ? 'warning' : 'neutral'}
          data-phase={vm.command.phase}
          role="status"
        >
          {PHASE_ICON[vm.command.phase]} {vm.command.kindText}: {vm.command.text}
        </p>
      )}

      {vm.feedback !== null && vm.feedback.error !== null && vm.feedback.phase !== 'done' && vm.feedback.phase !== 'sending' && (
        <div data-testid="aqsi-refund-feedback" data-phase={vm.feedback.phase}>
          <StatusMessage
            kind={vm.feedback.phase === 'unknown' ? 'unavailable' : 'error'}
            title={
              vm.feedback.phase === 'unknown'
                ? 'Исход неизвестен: повторять нельзя, проверьте статус'
                : 'Служба отказала в операции'
            }
            text={vm.feedback.error.message}
            meta={[vm.feedback.error.code, vm.feedback.error.correlationId].filter(Boolean).join(' · ')}
          />
          {vm.feedback.note !== null && <p className="aqsi-card__text">{vm.feedback.note}</p>}
          <div className="aqsi-card__actions">
            <Button variant="ghost" onClick={actions.dismissFeedback}>
              Понятно
            </Button>
          </div>
        </div>
      )}

      {vm.stage === 'blocked' && vm.blockedReason !== null && (
        <section className="aqsi-panel" data-tone="warning" aria-label="Возврат недоступен">
          <h3>
            <Ban size={16} aria-hidden="true" /> Вернуть отсюда нельзя
          </h3>
          <p className="aqsi-card__text">{vm.blockedReason}</p>
          {vm.blockedAction !== null && (
            <div className="aqsi-card__actions">
              <Button onClick={actions.openBlocked}>{BLOCKED_ACTION_TEXT[vm.blockedAction.kind]}</Button>
            </div>
          )}
        </section>
      )}

      {vm.stage === 'select' && (
        <form
          className="aqsi-form"
          aria-label="Что возвращается"
          onSubmit={(event) => {
            event.preventDefault()
            actions.create()
          }}
        >
          <p className="aqsi-caption">
            Цена, ставка и предмет расчёта — из чека; сумму возврата считает служба. Деньги вернутся способом исходной
            оплаты.
          </p>
          <ul className="sale__lines" aria-label="Позиции чека">
            {vm.lines.map((line) => (
              <SourceLine
                key={line.ordinal}
                line={line}
                locked={busy || vm.command?.phase === 'unknown'}
                onChange={(quantity) => actions.quantityChanged(line.ordinal, quantity)}
              />
            ))}
          </ul>
          {vm.createBlockedReason !== null && <p className="aqsi-card__text">{vm.createBlockedReason}</p>}
          <div className="aqsi-card__actions">
            <Button variant="ghost" onClick={actions.returnAll} disabled={busy}>
              Отметить всё
            </Button>
            <Button variant="ghost" onClick={actions.clear} disabled={busy}>
              Очистить
            </Button>
            <Button
              type="submit"
              variant="primary"
              icon={<Undo2 size={14} aria-hidden="true" />}
              disabled={!vm.canCreate}
              busy={busy}
            >
              Вернуть
            </Button>
          </div>
        </form>
      )}

      {vm.stage === 'refund' && vm.refund !== null && (
        <section className="aqsi-panel" aria-label="Ход возврата" data-tone={vm.operation?.tone ?? 'neutral'}>
          <h3>
            Возврат {vm.refund.totalText} {vm.refund.paymentText}
          </h3>
          <ul className="aqsi-refund__summary" aria-label="Позиции возврата">
            {vm.refund.lines.map((line) => (
              <li key={line.key}>
                {line.name} — {line.quantityText} · {line.amountText}
              </li>
            ))}
          </ul>
          <StateBadge badge={vm.refund.money} />
          <StateBadge badge={vm.refund.receipt} />
          {vm.refund.slipText !== null && <p className="aqsi-card__text">{vm.refund.slipText}</p>}
          {vm.refund.lastMessage !== null && <p className="aqsi-card__text">Касса: {vm.refund.lastMessage}</p>}
          {vm.refund.attentionLines.map((line) => (
            <p key={line} className="aqsi-card__text" role="note">
              <CircleAlert size={14} aria-hidden="true" /> {line}
            </p>
          ))}
          {vm.canFiscalize && (
            <p className="aqsi-card__text">Деньги возвращены, чек возврата ещё не пробит: служба отправит его сама. Кнопка ниже - на случай, если чек не ушёл.</p>
          )}
          {vm.operation !== null && (
            <div className="aqsi-form" aria-label="Операция" data-testid="aqsi-refund-operation">
              <p className="aqsi-card__state">
                {vm.operation.kindText}: {vm.operation.stateText}
                {vm.operation.polling && ' · проверяется автоматически'}
              </p>
              {vm.operation.details.map((line) => (
                <p key={line} className="aqsi-card__text">
                  {line}
                </p>
              ))}
              {vm.operation.manualRequired && (
                <p className="aqsi-card__text" role="note">
                  <CircleAlert size={14} aria-hidden="true" /> Срок команды истёк, а исход так и не подтверждён. Нужна
                  ручная сверка с причиной: проверьте терминал/кассу и кабинет. Повторять возврат нельзя.
                </p>
              )}
              <p className="aqsi-card__text">Операция {vm.operation.operationId}</p>
            </div>
          )}
          <div className="aqsi-card__actions">
            {vm.canPay && (
              <Button variant="primary" icon={<Wallet size={14} aria-hidden="true" />} onClick={actions.pay} busy={busy}>
                {vm.payText}
              </Button>
            )}
            {vm.canFiscalize && (
              <Button variant="primary" icon={<ReceiptText size={14} aria-hidden="true" />} onClick={actions.fiscalize} busy={busy}>
                Пробить чек возврата
              </Button>
            )}
            {vm.operation?.canReconcile === true && (
              <Button icon={<Search size={14} aria-hidden="true" />} onClick={actions.reconcile}>
                Сверить с кабинетом
              </Button>
            )}
            {vm.operation?.manualRequired === true && (
              <Button variant="primary" icon={<ShieldAlert size={14} aria-hidden="true" />} onClick={actions.openManualResolve}>
                Ручная сверка
              </Button>
            )}
            {vm.canCancel && (
              <Button variant="ghost" icon={<Ban size={14} aria-hidden="true" />} onClick={actions.cancel} disabled={busy}>
                Отменить возврат
              </Button>
            )}
          </div>
        </section>
      )}

      {vm.stage === 'finished' && vm.finished !== null && (
        <section className="aqsi-panel" aria-label="Итог возврата" role="status">
          <h3>
            {vm.finished.cancelled ? (
              <>
                <Ban size={16} aria-hidden="true" /> Возврат отменён — деньги не двигались
              </>
            ) : (
              <>
                <CheckCircle2 size={16} aria-hidden="true" /> Возврат завершён
              </>
            )}
          </h3>
          <p className="aqsi-card__text">
            {vm.finished.totalText} {vm.finished.paymentText}
            {vm.finished.receiptId !== null && ` · чек возврата ${vm.finished.receiptId}`}
          </p>
          <div className="aqsi-card__actions">
            <Button onClick={actions.startNew}>Ещё возврат по этому чеку</Button>
          </div>
        </section>
      )}

      <p className="aqsi-caption" role="note">
        <Info size={14} aria-hidden="true" /> {vm.reversalNote}
      </p>

      <div className="aqsi-card__actions">
        <Button variant="ghost" onClick={actions.close}>
          Закрыть
        </Button>
      </div>
    </section>
  )
}
